/**
 * The home's "Coming up" section — which items it shows, in which order, under which chip, and which
 * of its states the section is in. Spec: `artifacts/prototypes/home-v3/COMING-UP.md` (Oct 18 2026, and
 * the "Show all" modal added Oct 19).
 *
 * Shared 1:1 with iOS `ComingUp.swift` and Android `ComingUp.kt`; all three unit-test the same table.
 * Pure, with imports only from other import-free modules, so the test does not pull the app's graph.
 *
 * ## Where the items come from
 *
 * There is no "coming up" endpoint. The section is built on the client from three reads every surface
 * already makes for the home:
 *
 * - **tasks** — a task the agent will start at a set time (`isScheduledTask`: PENDING with a future
 *   `notBefore`) is an upcoming TASK; a task the shared bucket rule files under `waiting` (a parked task,
 *   or one blocked on an open question) and updated in the last 30 days is NEEDS YOU.
 * - **routines** — a routine that is on and has a `nextRun` is an upcoming ROUTINE, one card per routine
 *   (its next run only). A paused one, and a kit offer nobody switched on, never shows.
 * - **goals** — an open goal with an end date (`endsAt`) is a DEADLINE: upcoming while the date is ahead,
 *   OVERDUE once it has passed and the goal is still not done.
 *
 * Calendar events are not a source yet: no client reads the calendar's events (only the Meetings
 * channel's meetings, which are a different list). Empty slots fall back to the goal catalog
 * (`lib/goal-intents.ts`), the same five the goal picker offers.
 */
import { bucketOfTask, recentTasks, type BucketableTask } from './agent-task-buckets';
import { isScheduledTask } from './task-schedule';
import { routineIsOn, routineIsRecommended, type StatusableRoutine } from './routine-status';

/** The look-ahead window: the next 7 days. */
export const COMING_UP_WINDOW_MS = 7 * 86400 * 1000;
/** How many cards Home shows. Always three: empty slots become suggested goals. */
export const COMING_UP_CARDS = 3;
/** At most this many overdue deadlines, anywhere in the section. The rest stay in their Space. */
export const COMING_UP_OVERDUE_CAP = 2;
/** At most this many Needs-you cards while there is something else to show, so upcoming work keeps a card. */
export const COMING_UP_NEEDS_YOU_CAP = 2;

export type ComingUpKind = 'task' | 'routine' | 'deadline' | 'needsYou';

/** What tapping an item opens — the item's own drill-in. */
export type ComingUpTarget =
  | { type: 'task'; id: string }
  | { type: 'routine'; id: string }
  | { type: 'goal'; id: string; workspaceId?: string };

export interface ComingUpItem {
  /** Stable across reads: `<kind>:<source id>`. */
  id: string;
  kind: ComingUpKind;
  title: string;
  /** When it happens (task / routine run) or is due (deadline), ms. Absent on Needs you. */
  at?: number;
  /** When the source item was created, ms — the tie-break between two items at the same time. */
  createdAt?: number;
  /** The owning agent's id. Absent with `maestro` false means the item has no agent (a deadline). */
  agentId?: string;
  /** No agent named on it, but it is Maestro's own work. */
  maestro: boolean;
  workspaceId?: string;
  target: ComingUpTarget;
}

/** The chip on a card or a modal row. Clock text is formatted by the caller in the reader's own locale. */
export type ComingUpChip =
  | { kind: 'needsYou' }
  | { kind: 'overdue' }
  | { kind: 'today'; at: number }
  | { kind: 'tomorrow'; at: number }
  | { kind: 'inDays'; days: number }
  | { kind: 'routine'; at: number }
  | { kind: 'suggestedGoal' };

/** The button on a card or a row: View on scheduled items, Approve on Needs you, Start on goals. */
export type ComingUpAction = 'view' | 'approve' | 'startGoal';

// ── Inputs ───────────────────────────────────────────────────────────────────────────────────────────

export type ComingUpTaskInput = BucketableTask & {
  id: string;
  title?: string;
  workspaceId?: string;
  agentId?: string;
  createdDate?: string | Date;
};

export type ComingUpRoutineInput = StatusableRoutine & {
  id: string;
  name?: string;
  workspaceId?: string;
  agentId?: string | null;
  nextRun?: string | null;
  createdDate?: string;
};

export interface ComingUpGoalInput {
  id: string;
  title: string;
  status: string;
  outcome?: string;
  workspaceId?: string;
  endsAt?: string;
  parentGoalId?: string;
  createdDate?: string;
}

export interface ComingUpInputs {
  tasks: readonly ComingUpTaskInput[];
  routines: readonly ComingUpRoutineInput[];
  goals: readonly ComingUpGoalInput[];
  /** The ids of the Spaces the account holds, or null while unknown. An item in another Space (archived
   *  or deleted) is hidden; an item with no Space is kept. */
  liveWorkspaceIds: ReadonlySet<string> | null;
  now: number;
}

function ms(value: string | Date | null | undefined): number | undefined {
  if (!value) return undefined;
  const at = new Date(value).getTime();
  return Number.isNaN(at) ? undefined : at;
}

/** A goal is done once it reached ACHIEVED or carries any outcome (it settled, one way or another). */
function goalIsOpen(goal: ComingUpGoalInput): boolean {
  if (goal.outcome) return false;
  return goal.status !== 'ACHIEVED' && goal.status !== 'ABANDONED' && goal.status !== 'CANCELLED';
}

function inLiveSpace(workspaceId: string | undefined, live: ReadonlySet<string> | null): boolean {
  if (!workspaceId || !live) return true;
  return live.has(workspaceId);
}

/** Every candidate item, unsorted and uncapped. Exported for the tests. */
export function comingUpCandidates(input: ComingUpInputs): ComingUpItem[] {
  const { now, liveWorkspaceIds } = input;
  const horizon = now + COMING_UP_WINDOW_MS;
  const items: ComingUpItem[] = [];
  const seen = new Set<string>();
  const push = (item: ComingUpItem) => {
    if (seen.has(item.id) || !inLiveSpace(item.workspaceId, liveWorkspaceIds)) return;
    seen.add(item.id);
    items.push(item);
  };

  for (const task of input.tasks) {
    if (!task.id) continue;
    const agentId = (task.agentId ?? '').trim() || undefined;
    const base = {
      title: task.title ?? '',
      createdAt: ms(task.createdDate),
      agentId,
      maestro: !agentId,
      workspaceId: task.workspaceId || undefined,
      target: { type: 'task', id: task.id } as ComingUpTarget,
    };
    if (bucketOfTask(task) === 'waiting') {
      // The same 30-day window the Tasks screen's "Waiting on you" uses: a question left for months is
      // not "coming up", and the reader would meet it here but nowhere else.
      if (recentTasks([task], now).length === 0) continue;
      push({ ...base, id: `task:${task.id}`, kind: 'needsYou' });
      continue;
    }
    if (isScheduledTask(task, now)) {
      const at = ms(task.notBefore);
      if (at !== undefined && at <= horizon) push({ ...base, id: `task:${task.id}`, kind: 'task', at });
    }
  }

  for (const routine of input.routines) {
    if (!routine.id || !routineIsOn(routine) || routineIsRecommended(routine)) continue;
    const at = ms(routine.nextRun);
    if (at === undefined || at < now || at > horizon) continue;
    const agentId = (routine.agentId ?? '').trim() || undefined;
    push({
      id: `routine:${routine.id}`,
      kind: 'routine',
      title: routine.name ?? '',
      at,
      createdAt: ms(routine.createdDate),
      agentId,
      maestro: !agentId,
      workspaceId: routine.workspaceId || undefined,
      target: { type: 'routine', id: routine.id },
    });
  }

  for (const goal of input.goals) {
    if (!goal.id || !goalIsOpen(goal)) continue;
    const at = ms(goal.endsAt);
    if (at === undefined || at > horizon) continue;
    // A deadline names no agent: the Space alone is its second line.
    push({
      id: `goal:${goal.id}`,
      kind: 'deadline',
      title: goal.title,
      at,
      createdAt: ms(goal.createdDate),
      maestro: false,
      workspaceId: goal.workspaceId || undefined,
      target: { type: 'goal', id: goal.id, workspaceId: goal.workspaceId },
    });
  }

  return items;
}

/** Overdue = a deadline whose date has passed and that is still open. */
export function isOverdue(item: ComingUpItem, now: number): boolean {
  return item.kind === 'deadline' && item.at !== undefined && item.at < now;
}

const byCreated = (a: ComingUpItem, b: ComingUpItem) => (a.createdAt ?? Infinity) - (b.createdAt ?? Infinity);
const byTimeThenCreated = (a: ComingUpItem, b: ComingUpItem) => (a.at ?? 0) - (b.at ?? 0) || byCreated(a, b);

export interface ComingUpLists {
  /** What the three cards show (real items only, at most three, in order). */
  cards: ComingUpItem[];
  /** What the "Show all" modal lists: every item, same order, overdue still capped. */
  all: ComingUpItem[];
}

/**
 * The order is Needs you, then overdue, then upcoming soonest first; a tie at the same time goes to the
 * one created first. Overdue is capped at two everywhere. Needs you is capped at two on the cards only
 * while something else is waiting for a card; with nothing else, it fills all three.
 */
export function buildComingUp(input: ComingUpInputs): ComingUpLists {
  const candidates = comingUpCandidates(input);
  const needs = candidates.filter((i) => i.kind === 'needsYou').sort(byCreated);
  const overdue = candidates
    .filter((i) => isOverdue(i, input.now))
    .sort(byTimeThenCreated)
    .slice(0, COMING_UP_OVERDUE_CAP);
  const upcoming = candidates
    .filter((i) => i.kind !== 'needsYou' && !isOverdue(i, input.now))
    .sort(byTimeThenCreated);

  const rest = [...overdue, ...upcoming];
  let cards = [...needs.slice(0, COMING_UP_NEEDS_YOU_CAP), ...rest].slice(0, COMING_UP_CARDS);
  // Fewer than three means all of `rest` fits, so the free slots go back to Needs you, still first.
  if (cards.length < COMING_UP_CARDS) {
    cards = [...needs.slice(0, COMING_UP_NEEDS_YOU_CAP + COMING_UP_CARDS - cards.length), ...rest].slice(0, COMING_UP_CARDS);
  }
  return { cards, all: [...needs, ...rest] };
}

/** How many suggested goals fill the empty slots. */
export function goalSlots(cards: number): number {
  return Math.max(0, COMING_UP_CARDS - cards);
}

/** The goal suggestions still on offer: the catalog minus the ones already started or dismissed. */
export function availableGoalIds<T extends string>(catalog: readonly T[], used: ReadonlySet<string>): T[] {
  return catalog.filter((id) => !used.has(id));
}

// ── Chips ────────────────────────────────────────────────────────────────────────────────────────────

/** Whole calendar days from `now` to `at`, in the reader's own time zone (the device's). */
export function calendarDaysBetween(now: number, at: number): number {
  const a = new Date(now);
  const b = new Date(at);
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((end - start) / 86400000);
}

export function comingUpChip(item: ComingUpItem, now: number): ComingUpChip {
  if (item.kind === 'needsYou') return { kind: 'needsYou' };
  if (isOverdue(item, now)) return { kind: 'overdue' };
  const at = item.at ?? now;
  if (item.kind === 'routine') return { kind: 'routine', at };
  const days = calendarDaysBetween(now, at);
  if (days <= 0) return { kind: 'today', at };
  if (days === 1) return { kind: 'tomorrow', at };
  return { kind: 'inDays', days };
}

export function comingUpAction(item: ComingUpItem): ComingUpAction {
  return item.kind === 'needsYou' ? 'approve' : 'view';
}

// ── States ───────────────────────────────────────────────────────────────────────────────────────────

export type ComingUpRead = 'loading' | 'ready' | 'error';

/**
 * - `loading` (E) — the first load: three placeholder cards, never "All clear".
 * - `failed` (G) — every source failed: one error block with Retry. Never `allClear` here.
 * - `items` (A, B) — at least one real item; goal cards fill the rest.
 * - `pickGoal` (C) — nothing real, but goals to suggest.
 * - `allClear` (D) — nothing real and no goal left to suggest.
 *
 * `partial` (F) rides beside `items`/`pickGoal`/`allClear`: a source failed while another answered, so
 * the section shows what it has and a "Some items could not load. Retry" line.
 */
export type ComingUpState = 'loading' | 'failed' | 'items' | 'pickGoal' | 'allClear';

export function comingUpState(
  reads: readonly ComingUpRead[],
  realItems: number,
  goalsLeft: number,
): { state: ComingUpState; partial: boolean } {
  if (reads.length > 0 && reads.every((r) => r === 'error')) return { state: 'failed', partial: false };
  if (reads.some((r) => r === 'loading')) return { state: 'loading', partial: false };
  const partial = reads.some((r) => r === 'error');
  if (realItems > 0) return { state: 'items', partial };
  return { state: goalsLeft > 0 ? 'pickGoal' : 'allClear', partial };
}

/** "Show all" shows only when the section shows real items (state A or B). */
export function showsShowAll(state: ComingUpState): boolean {
  return state === 'items';
}
